// @flow

import React from 'react';
import bemify, { toModifiers } from 'bemify-js';

import { pluralize } from '@zapier/common-utils';

import Button from 'app/common/components/Button';
import Icon from 'app/common/components/Icon';
import Link from 'app/common/components/Link';
import Tag from 'app/common/components/Tag';
import {
  getDaysRemainingMessage,
  getPropsForZapWarning,
} from 'app/dashboard/utils/ZapWarningUtils';
import 'app/dashboard/components/ZapWarning.scss';

const bem = bemify('zap-warning');

type Props = {
  heldTasksStartsExpiringOn: string,
  heldTasksCount: number,
  staleAuthServiceNamesByZap: string[],
  onHeldTaskLinkClick: () => void,
  onFixItLinkClick: () => void,
};

const HeldTaskComponent = ({
  daysRemainText,
  msg,
  heldTasksCount,
  onHeldTaskLinkClick,
  onFixItLinkClick,
}) => {
  return (
    <div className={bem()}>
      <Tag
        className={bem('__tag')}
        kind="warning"
        onClick={onHeldTaskLinkClick}
      >
        {heldTasksCount}
        &nbsp;
        {pluralize('Task', heldTasksCount)} held
      </Tag>
      <Icon className={bem('__icon')} type="clock" />
      <span>
        <span className={bem('__message', toModifiers('bold'))}>
          {daysRemainText}
          &nbsp;
        </span>
        {msg}
      </span>
      &nbsp;
      <Button
        className={bem('__button')}
        kind="as-text"
        onClick={onHeldTaskLinkClick}
      >
        See {pluralize('Task', heldTasksCount)}
      </Button>
      <span>&nbsp;or&nbsp;</span>
      <Button
        className={bem('__button')}
        kind="as-text"
        onClick={onFixItLinkClick}
      >
        Help Me Fix It.
      </Button>
    </div>
  );
};

const WarningComponent = ({ tagMsg, msg, linkUrl, linkText }) => {
  if (!msg) {
    return null;
  }

  return (
    <div className={bem()}>
      <Link className={bem('__tag-link')} href={linkUrl}>
        <Tag className={bem('__tag')} kind="premium">
          {tagMsg}
        </Tag>
      </Link>
      <span>{msg}</span>
      <Link className={bem('__link')} href={linkUrl}>
        {linkText}
      </Link>
    </div>
  );
};

const ZapWarning = ({
  heldTasksStartsExpiringOn,
  heldTasksCount,
  staleAuthServiceNamesByZap,
  onHeldTaskLinkClick,
  onFixItLinkClick,
}: Props) => {
  if (heldTasksCount) {
    const { daysRemainText, msg } = getDaysRemainingMessage(
      heldTasksStartsExpiringOn
    );

    const props = {
      daysRemainText,
      msg,
      heldTasksCount,
      onHeldTaskLinkClick,
      onFixItLinkClick,
    };

    return <HeldTaskComponent {...props} />;
  }

  const hasStaleAuth = staleAuthServiceNamesByZap.length > 0;
  if (hasStaleAuth) {
    const props = getPropsForZapWarning({ staleAuthServiceNamesByZap });

    return <WarningComponent {...props} />;
  }

  return null;
};

export default ZapWarning;
